import { IModalService } from "angular-ui-bootstrap";
import { TaskService } from "@gtmhub/tasks/services/taskService";
import { InsightService } from "@gtmhub/insights";
import { ICloneOrMoveGoalScope, IConfirmCloneOrMoveModal, IGoalCloneSelected } from "@gtmhub/goals/controllers/clone-or-move-goal";
import { IControllerService, IPromise, IQService, IRootScopeService } from "angular";
import { ContributorType, IGoal } from "@gtmhub/goals/models";
import { IMetric, MetricService } from "@gtmhub/okrs/metrics";
import { getCurrentUserId } from "@gtmhub/users";
import { handleRequestsInBatches } from "@gtmhub/util";
import { prepareCheckinData } from "@gtmhub/web-workers/shared";
import { CloneOkrsMode, IMetricCloneSelected, OkrEvents } from "@gtmhub/okrs";
import { IStatusData } from "@gtmhub/shared/components/status-bar/status-bar.component";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { StatusBarService } from "@gtmhub/shared/components";
import { GoalService } from "@gtmhub/goals/goal.service";
import { calculateOrderIdWhenCloningIntoSameParent, ICloneOkrsOrderId } from "./utils";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { CloneResourceService } from "@gtmhub/cloning/clone-resource.service";

export class CloneOkrsService {
  public static $inject = [
    "$rootScope",
    "$controller",
    "$q",
    "$uibModal",
    "MetricService",
    "InsightService",
    "TaskService",
    "StatusBarService",
    "UIErrorHandlingService",
    "GoalService",
    "CloneResourceService",
    "FeatureToggleService",
  ];

  private okrEvents: OkrEvents;

  private refactorSessionCloning: boolean;

  constructor(
    private $rootScope: IRootScopeService,
    private $controller: IControllerService,
    private $q: IQService,
    private $uibModal: IModalService,
    private metricService: MetricService,
    private insightService: InsightService,
    private taskService: TaskService,
    private statusBarService: StatusBarService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private goalService: GoalService,
    private cloneResourceService: CloneResourceService,
    private featureToggleService: FeatureToggleService
  ) {
    this.okrEvents = new OkrEvents(this.$rootScope);
    this.featureToggleService.isFeatureAvailable("refactor-session-cloning").then((enabled) => {
      this.refactorSessionCloning = enabled;
    });
  }

  // not used anywhere up to this point
  cloneGoal(meta: { goal: IGoal; opts: IGoalCloneSelected }): void {
    if (this.refactorSessionCloning) {
      this.cloneResourceService.cloneGoal(meta.goal, meta.opts);
      return;
    }

    const $scope = this.$rootScope.$new() as ICloneOrMoveGoalScope;
    this.$controller("CloneOrMoveGoalCtrl", { $scope, goal: meta.goal });
    $scope.cloneOrMove("clone", meta.opts);
  }

  confirmClone(meta: { cfCount: number; mode: CloneOkrsMode }): IPromise<void> {
    return this.$uibModal.open({
      templateUrl: require("../../../goals/views/modules/confirm-clone-or-move.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope: IConfirmCloneOrMoveModal) {
          $scope.method = "clone";
          $scope.cfCounter = meta.cfCount;
          $scope.invokerType = meta.mode;
        },
      ],
    }).result;
  }

  cloneMetric(meta: { metric: IMetric; opts: IMetricCloneSelected }): IPromise<void> {
    if (this.refactorSessionCloning) {
      return this.cloneResourceService.cloneMetric(meta.metric, meta.opts);
    }
    return this.metricService
      .getMetric(meta.metric.id)
      .then((originalMetric) => {
        this.statusBarService.show();

        const isClonedIntoSameGoalParent = originalMetric.goalId === meta.opts.goal.id;
        if (isClonedIntoSameGoalParent) {
          return this.getNewMetricOrderIdBasedOnGoalContributors(originalMetric).then(({ shouldSetOrderId, newOrderId }) => {
            if (shouldSetOrderId) {
              originalMetric.orderId = newOrderId;
            } else {
              delete originalMetric.orderId;
            }
            return this.proceedWithCloningAction(meta, originalMetric);
          });
        }

        return this.proceedWithCloningAction(meta, originalMetric);
      })
      .catch((error) => {
        this.handleCloningError(error);
      });
  }

  private proceedWithCloningAction = (meta: { metric: IMetric; opts: IMetricCloneSelected }, originalMetric: IMetric) => {
    const newMetric = this.processMetric(originalMetric, meta.opts);

    let params: Record<string, boolean>;
    if (!meta.opts.notifications) {
      params = { skipNotification: true };
    }

    if (meta.opts.duplicateInsight) {
      return this.insightService.duplicateInsight(originalMetric.insightName).then((newInsight) => {
        newMetric.insightName = newInsight.name;
        return this.metricService.createMetric(newMetric, params).then((createdMetric) => this.afterClone(originalMetric, createdMetric, meta.opts));
      });
    } else {
      return this.metricService.createMetric(newMetric, params).then((createdMetric) => this.afterClone(originalMetric, createdMetric, meta.opts));
    }
  };

  private afterClone(originalMetric: IMetric, clonedMetric: IMetric, opts: IMetricCloneSelected): IPromise<void> {
    const tasksPromise = this.taskService.cloneTasksRecursively(
      { currentPage: 1, limit: 100 },
      { newParentIdsMap: { [originalMetric.id]: clonedMetric.id }, parentIds: [originalMetric.id] }
    );
    const checkinsPromise = opts.progress ? this.cloneCheckins(originalMetric, clonedMetric) : this.$q.resolve();

    return this.$q.all([tasksPromise, checkinsPromise]).then(
      () => {
        this.okrEvents.broadcastGoalsChanged("goalChanged");
        this.$rootScope.$broadcast("goalsChanged");

        const data: IStatusData = {
          status: "done_cloning_metric",
          params: {
            metric: clonedMetric,
            targetGoal: opts.goal,
            ...(opts.session.id !== originalMetric.sessionId && { targetSession: opts.session }),
          },
        };

        this.statusBarService.updateStatus(data);
      },
      (error) => {
        this.handleCloningError(error);
      }
    );
  }

  private handleCloningError(error: IUIError): void {
    this.statusBarService.hide();
    this.uiErrorHandlingService.handleModal(error);
  }

  private cloneCheckins(originalMetric: IMetric, clonedMetric: IMetric): IPromise<void> {
    const createCheckinPromise = (item) => this.metricService.checkin(clonedMetric, item.checkin);

    return handleRequestsInBatches(prepareCheckinData([originalMetric], getCurrentUserId()), createCheckinPromise, 1, this.$q);
  }

  private processMetric(originalMetric: IMetric, opts: IMetricCloneSelected): IMetric {
    const newMetric = { ...originalMetric, goalId: opts.goal.id, name: `${originalMetric.name} clone` };
    delete newMetric.id;
    // It will be updated by the checkins
    delete newMetric.confidence;

    // If we wanna keep the current progress, checkins will update the actual value to the current one
    // But this way we will create the moments correctly
    newMetric.actual = !opts.progress ? 0 : newMetric.initialValue;

    if (opts.sync) {
      newMetric.sourceMetricId = originalMetric.id;
      //  We cannot have a metric which is both linked and dynamic
      if (newMetric.insightName) {
        delete newMetric.insightName;
        delete newMetric.dynamic;
        newMetric.manualType = "double";
      }
    }

    return newMetric;
  }

  private getNewMetricOrderIdBasedOnGoalContributors = (originalMetric: IMetric): IPromise<ICloneOkrsOrderId> => {
    const mongoGoalsFilter = {
      filter: { parentId: { $in: [originalMetric.goalId] } },
      fields: "orderId",
    };
    const mongoMetricsFilter = {
      filter: { goalId: { $in: [originalMetric.goalId] } },
      fields: "orderId",
    };

    return this.$q
      .all({
        subgoalsRes: this.goalService.getGoalsV2(mongoGoalsFilter),
        metricsRes: this.metricService.getMetricsV2(mongoMetricsFilter),
      })
      .then(
        ({ subgoalsRes, metricsRes }) => {
          const contributors: ContributorType[] = [...subgoalsRes.items, ...metricsRes.items];
          return calculateOrderIdWhenCloningIntoSameParent(contributors, originalMetric.orderId);
        },
        (error) => {
          this.handleCloningError(error);
          return this.$q.resolve({ shouldSetOrderId: false });
        }
      );
  };
}
